/**
 * Retrieval pipeline step that queries a CDF data-model view and produces a
 * typed {@link Dataset}. Translates the canonical {@link rootFilterSchema}
 * AST into CDF's native instance-query filter format, executes via
 * {@link executeQueryWithRecovery}, flattens the nested CDF node structure,
 * and enriches timeseries-reference columns with latest values in a single
 * step boundary.
 *
 * Semantics:
 *
 * - Takes a flat `viewExternalId` + `properties?` + canonical `filters?` +
 *   `sort?` + `limit`. The tool-layer adapter is responsible for translating
 *   the LLM's view-selection shape into these flat params.
 * - Translator pushes down what CDF can express natively (`equals/eq →
 *   equals`, `in → in`, `startsWith → prefix`, `gt/gte/lt/lte → range`,
 *   `between → range`, and the and/or/not group wrappers). Ops CDF cannot
 *   express (contains, regex, notEquals, endsWith, notIn, isNull, etc.)
 *   throw a {@link PartitionInvariantError} when reached at translate time
 *   — `partitionFilter` should split them off as a residual and apply them
 *   client-side through the transformDataset filter operator.
 * - Column schema is sourced authoritatively from
 *   {@link columnsFromView}. Timeseries-ref columns keep their declared
 *   `type: 'ref'` / `refKind: 'ts'` metadata; only their row-level value
 *   shape is replaced by an {@link EnrichedTimeseriesRef}
 *   (`{ space, externalId, unit, datapoints, start, end, latestDatapoint }`)
 *   carrying the most recent raw datapoints. Any extra columns introduced
 *   by the flattened CDF node structure (e.g. `space`, `externalId`) are
 *   appended via {@link inferColumnSchemas} so downstream operators see
 *   the full column set.
 * - `consumesInput` is `false`; the orchestrator passes `undefined` for
 *   input.
 *
 * Requires `client`, `queryClient`, and `views` on the
 * {@link PipelineContext}. All three are validated at the top of
 * {@link execute}.
 */
import type { CogniteClient, QueryRequest } from '@cognite/sdk';
import z from 'zod';

import { instanceReferenceSchema } from '#core/lib/cognite/types.ts';
import type { DataModelView } from '#core/lib/cognite/view.ts';
import { extractViewColumnTypes } from '#core/lib/cognite/view.ts';
import { columnsFromView, inferColumnSchemas, viewColumnTypeToCanonical } from '#core/lib/transforms/column-schema.ts';
import { partition } from '#core/lib/utils/array.ts';

import { createDataset } from '../dataset.ts';
import { evaluateFilterTree } from '../filter/evaluate.ts';
import {
  type ResolvedArray,
  type ResolvedFilterNode,
  type ResolvedLeaf,
  type ResolvedLevel1Group,
  type ResolvedRootFilter,
  type ResolvedScalar,
  resolveFilterSourceRefs,
} from '../filter/resolve-source-refs.ts';
import { type FilterOp, rootFilterSchema } from '../filter/schema.ts';
import type { PipelineStep } from '../step/types.ts';
import type { ColumnSchema } from '../types.ts';
import { executeQueryWithRecovery, KG_QUERY_MAX_LIMIT } from '#plugin-chat/lib/query/error-recovery.ts';
import { InvalidSpecError, PartitionInvariantError, ViewNotFoundError } from '#plugin-chat/lib/tool/errors/index.ts';

const cdfPropertyValueSchemaPrimitives = [z.string(), z.number(), instanceReferenceSchema];
const cdfPropertyValueSchema = z.union([
  ...cdfPropertyValueSchemaPrimitives,
  ...cdfPropertyValueSchemaPrimitives.map((s) => s.array()),
]);

/**
 * Schema that flattens CDF node/edge nested properties structure into a flat record.
 *
 * Extracts properties from nested `properties[space][table][key]` structure and merges
 * them with top-level metadata fields (space, externalId, timestamps).
 *
 * FIXME: Parse data using view wrappers
 */
const flattenedCdfNodeSchema = z
  .object({
    space: z.string(),
    externalId: z.string(),
    createdTime: z.coerce.date(),
    lastUpdatedTime: z.coerce.date(),
    properties: z.record(z.string(), z.record(z.string(), z.record(z.string(), cdfPropertyValueSchema))),
  })
  .transform(({ properties, ...rest }) => {
    const spaceData = Object.values(properties)[0] ?? {};
    const tableData = Object.values(spaceData)[0] ?? {};
    return { ...rest, ...tableData };
  });

const STEP_ID = 'queryKnowledgeGraph';

/**
 * Canonical filter ops that CDF's instance-query filter language can evaluate
 * server-side. Any op NOT in this list is applied client-side in the query
 * step after the CDF round-trip — see {@link partitionFilter} and the
 * in-step handoff to {@link filterStep}.
 *
 * Adding a new pushable op requires: (a) adding it here, and (b) adding its
 * translator to {@link LEAF_TRANSLATORS}. The compiler enforces the two
 * lists stay in sync via the `Record<CdfPushableOp, LeafTranslator>` typing.
 */
export const CDF_PUSHABLE_OPS = ['equals', 'eq', 'in', 'startsWith', 'gt', 'gte', 'lt', 'lte'] as const;
type CdfPushableOp = (typeof CDF_PUSHABLE_OPS)[number];
const CDF_PUSHABLE_OPS_SET: ReadonlySet<FilterOp> = new Set(CDF_PUSHABLE_OPS);

const querySortSchema = z.object({
  column: z.string().describe('Column name to sort by.'),
  direction: z.enum(['asc', 'desc']).default('asc').describe('Sort direction (default: asc).'),
});

export const queryKnowledgeGraphParamsSchema = z.object({
  viewExternalId: z.string().describe('CDF data model view externalId to query.'),
  filters: rootFilterSchema.optional(),
  sort: z.array(querySortSchema).optional().describe('Server-side sort applied by CDF before returning instances.'),
  limit: z
    .number()
    .int()
    .max(KG_QUERY_MAX_LIMIT)
    .default(KG_QUERY_MAX_LIMIT)
    .describe(`Maximum total instances to retrieve across pagination. Default and max: ${KG_QUERY_MAX_LIMIT}.`),
  title: z.string().optional().describe('Optional human-readable title attached to the produced Dataset.meta.'),
});

type QueryKnowledgeGraphParams = z.infer<typeof queryKnowledgeGraphParamsSchema>;

type LeafTranslator = (leaf: ResolvedLeaf, property: unknown) => unknown;

/**
 * Data-driven lookup from canonical filter op to its CDF leaf-filter form.
 * The type binds the key set to {@link CDF_PUSHABLE_OPS}, so adding or
 * removing a pushable op in one place forces the matching change here at
 * compile time. Callers never reach {@link translateLeaf} with a
 * non-pushable op — {@link partitionFilter} splits those out first.
 */
const LEAF_TRANSLATORS: Record<CdfPushableOp, LeafTranslator> = (() => {
  // equals/eq are canonical aliases — share one translator definition.
  // The resolved value's `value` field is a primitive for literal/string/number/date
  // resolutions and a `{space, externalId}` object for ref resolutions. CDF's
  // instance-query filter accepts both shapes directly.
  const equalsTranslator: LeafTranslator = (leaf, property) => ({
    equals: { property, value: scalarValue(leaf) },
  });
  return {
    equals: equalsTranslator,
    eq: equalsTranslator,
    in: (leaf, property) => ({ in: { property, values: arrayValues(leaf) } }),
    startsWith: (leaf, property) => ({ prefix: { property, value: patternString(leaf) } }),
    gt: (leaf, property) => ({ range: { property, gt: scalarValue(leaf) } }),
    gte: (leaf, property) => ({ range: { property, gte: scalarValue(leaf) } }),
    lt: (leaf, property) => ({ range: { property, lt: scalarValue(leaf) } }),
    lte: (leaf, property) => ({ range: { property, lte: scalarValue(leaf) } }),
  };
})();

/** Extract the primitive (or ref) value from a scalar-bearing resolved leaf.
 * CDF accepts ref values as `{space, externalId}` objects directly. */
function scalarValue(leaf: ResolvedLeaf): unknown {
  if (!('value' in leaf)) {
    return undefined;
  }
  const v = leaf.value as ResolvedScalar;
  if ('ref' in v) {
    return v.ref;
  }
  if ('string' in v) {
    return v.string;
  }
  if ('number' in v) {
    return v.number;
  }
  if ('date' in v) {
    return v.date;
  }
  return undefined;
}

/** Extract the array values (primitives or refs) from a set-bearing resolved leaf. */
function arrayValues(leaf: ResolvedLeaf): readonly unknown[] {
  if (!('value' in leaf)) {
    return [];
  }
  const v = leaf.value as ResolvedArray;
  if ('refs' in v) {
    return v.refs;
  }
  if ('array' in v) {
    return v.array;
  }
  return [];
}

/** Extract the literal pattern string from a pattern-op resolved leaf. */
function patternString(leaf: ResolvedLeaf): string {
  return 'literal' in leaf && 'string' in leaf.literal ? leaf.literal.string : '';
}

function translateGroup(
  group: ResolvedRootFilter | ResolvedLevel1Group,
  viewProperty: Record<string, unknown>,
): unknown {
  const clauses = group.filters.map((f) =>
    'op' in f ? translateLeaf(f, viewProperty) : translateGroup(f, viewProperty),
  );
  if (group.type === 'not') {
    return { not: clauses.length === 1 ? clauses[0] : { and: clauses } };
  }
  return { [group.type]: clauses };
}

function translateLeaf(leaf: ResolvedLeaf, viewProperty: Record<string, unknown>): unknown {
  const property = viewProperty[leaf.column];
  if (property === undefined || property === null) {
    throw new InvalidSpecError(`Filter column "${leaf.column}" is not a property of the view.`, {
      suggestions: [`Check spelling and confirm "${leaf.column}" exists on the view.`],
    });
  }
  // Invariant: partitionFilter ensures only pushable ops reach this function.
  // A missing translator here signals a programming error in the partitioner.
  const translator = LEAF_TRANSLATORS[leaf.op as CdfPushableOp];
  if (!translator) {
    throw new PartitionInvariantError(
      `Internal error: non-pushable op "${leaf.op}" reached translateLeaf.`,
      leaf.column,
    );
  }
  return translator(leaf, property);
}

/** Translate a resolved rootFilter into the CDF instance-query filter format. */
export function translateRootFilterToCdf(filter: ResolvedRootFilter, view: DataModelView): unknown {
  return translateGroup(filter, view.property as Record<string, unknown>);
}

function _getTimeseriesRefColumns(view: DataModelView): string[] {
  return Array.from(extractViewColumnTypes(view)?.entries() ?? [])
    .filter(([, v]) => v.type === 'instanceId' && v.referencedSchema === 'TimeSeries')
    .map(([k]) => k);
}

function buildInstanceQuery(
  params: Omit<QueryKnowledgeGraphParams, 'filters'> & { filters?: ResolvedRootFilter },
  view: DataModelView,
): unknown {
  const viewProperty = view.property as Record<string, unknown>;

  const filterClauses = [
    ...(params.filters ? [translateRootFilterToCdf(params.filters, view)] : []),
    { hasData: [view] },
  ];

  const sortSpec = params.sort?.map((s) => ({
    property: viewProperty[s.column],
    direction: s.direction,
  }));

  return {
    with: {
      [params.viewExternalId]: {
        nodes: filterClauses?.length ? { filter: { and: filterClauses } } : {},
        limit: params.limit,
        ...(sortSpec ? { sort: sortSpec } : {}),
      },
    },
    select: {
      [params.viewExternalId]: {
        sources: [
          {
            source: view,
            properties: ['*'],
          },
        ],
      },
    },
  };
}

async function runInstanceQuery(
  instanceQuery: QueryRequest,
  client: CogniteClient,
): Promise<Record<string, unknown>[]> {
  // executeQueryWithRecovery surfaces the CDF response's `items` dict
  // (view-keyed: `{ TestView: [node, ...] }`) directly as `RecoveryResult.items`
  // — so T is the view-keyed record, not a further `{ items: ... }` wrapper.
  const recoveryResult = await executeQueryWithRecovery<Record<string, unknown[]>>(instanceQuery, {
    client,
    maxRetries: 1,
    onRetry: (attempt, relaxedQuery) => {
      console.debug(`${STEP_ID} retry ${attempt}:`, relaxedQuery);
    },
  });

  if (!recoveryResult.success) {
    throw new InvalidSpecError(`Query failed after ${recoveryResult.attempts} attempts: ${recoveryResult.error}`, {
      suggestions: [
        'Make sure the query is correct and you are referencing fields based on the view schema.',
        'Try a simpler query without filters.',
        ...(recoveryResult.suggestedAlternatives?.map((a) => a.description) ?? []),
      ],
    });
  }

  // A query that matched zero instances is a valid empty result — surface
  // it as an empty dataset with a warning on meta (see queryKnowledgeGraphStep),
  // not as a hard error. The LLM still needs to know zero rows matched in
  // order to reason about the query; the warning carries that signal.
  const firstViewItems = Object.values(recoveryResult.items ?? {}).at(0) ?? [];
  return flattenedCdfNodeSchema.array().parse(firstViewItems);
}

function buildColumnSchemas(
  view: DataModelView,
  properties: readonly string[] | undefined,
  data: readonly Record<string, unknown>[],
): ColumnSchema[] {
  const viewColumns = columnsFromView(view, properties);
  const viewColumnNames = new Set(viewColumns.map((c) => c.name));
  const extraColumns = inferColumnSchemas(data).filter((c) => !viewColumnNames.has(c.name));
  return [...viewColumns, ...extraColumns];
}

/**
 * Split a canonical {@link RootFilter} into a pushable subtree (forwarded to
 * CDF server-side) and a residual subtree (evaluated client-side via
 * {@link filterStep} over the CDF result).
 *
 * Partitioning rules:
 *
 * - Root type `and`: children are classified individually. Leaves go server
 *   or client side based on {@link CDF_PUSHABLE_OPS}. Nested level-1 `and`
 *   groups are split leaf-by-leaf (equivalent to flattening — AND is
 *   associative, and root is AND). Nested `or`/`not` groups are treated
 *   atomically: all-pushable → pushable side; any non-pushable → residual.
 * - Root type `or` or `not`: any non-pushable op anywhere in the tree
 *   forces the entire filter to the residual side. Boolean logic rules
 *   out a safe split in this case.
 */
export function partitionFilter(root: ResolvedRootFilter): {
  pushable?: ResolvedRootFilter;
  residual?: ResolvedRootFilter;
} {
  if (root.type !== 'and') {
    return root.filters.every(isPushableNode) ? { pushable: root } : { residual: root };
  }
  const splits = root.filters.map(splitChild);
  return {
    pushable: wrapAnd(splits.flatMap((s) => (s.pushable ? [s.pushable] : []))),
    residual: wrapAnd(splits.flatMap((s) => (s.residual ? [s.residual] : []))),
  };
}

/** True when every leaf inside this AST node (leaf or level-1 group) is pushable to CDF. */
function isPushableNode(node: ResolvedFilterNode): boolean {
  return 'op' in node ? CDF_PUSHABLE_OPS_SET.has(node.op) : node.filters.every((l) => CDF_PUSHABLE_OPS_SET.has(l.op));
}

/**
 * Classify a direct child of a root-level AND into its pushable/residual
 * halves. Leaves and atomic level-1 groups (`or`/`not`) go one side or the
 * other as a whole; level-1 `and` groups are split leaf-by-leaf, since AND
 * nested inside AND is associative.
 */
function splitChild(child: ResolvedFilterNode): {
  pushable?: ResolvedFilterNode;
  residual?: ResolvedFilterNode;
} {
  if ('op' in child) {
    return CDF_PUSHABLE_OPS_SET.has(child.op) ? { pushable: child } : { residual: child };
  }
  if (child.type === 'and') {
    const [pushLeaves, resLeaves] = partition(child.filters, (l) => CDF_PUSHABLE_OPS_SET.has(l.op));
    return {
      pushable: pushLeaves.length > 0 ? { type: 'and', filters: pushLeaves } : undefined,
      residual: resLeaves.length > 0 ? { type: 'and', filters: resLeaves } : undefined,
    };
  }
  return isPushableNode(child) ? { pushable: child } : { residual: child };
}

/** Wrap a list of AND-child nodes as a root AND filter, or return undefined when empty. */
function wrapAnd(filters: ResolvedFilterNode[]): ResolvedRootFilter | undefined {
  return filters.length > 0 ? { type: 'and', filters } : undefined;
}

export const queryKnowledgeGraphStep: PipelineStep<QueryKnowledgeGraphParams> = {
  id: STEP_ID,
  description:
    'Retrieve instance data from a CDF data model view with optional server-side filters, sort, and projection. Translates the canonical rootFilter AST into CDF instance-query filter format.',
  paramsSchema: queryKnowledgeGraphParamsSchema,
  consumesInput: false,
  execute: async (params, _input, ctx) => {
    const { client, views, resolve } = ctx;

    const view = views.find((v) => v.externalId === params.viewExternalId);
    if (!view) {
      throw new ViewNotFoundError(
        params.viewExternalId,
        views.map((v) => v.externalId),
      );
    }

    // Resolve sourceRef leaves once at the top — every downstream consumer
    // (partition, CDF translator, residual evaluator) sees the resolved shape
    // with no `??` fallbacks and no raw sourceRef references.
    const resolvedFilter = params.filters
      ? await resolveFilterSourceRefs(params.filters, {
          resolve,
          getTargetType: (columnName: string) => {
            const viewColumnTypes = extractViewColumnTypes(view);
            const info = viewColumnTypes?.get(columnName);
            return info ? viewColumnTypeToCanonical(info.type) : undefined;
          },
          stepId: STEP_ID,
          contextLabel: `view "${params.viewExternalId}"`,
        })
      : undefined;

    // Split the resolved filter into what CDF can evaluate server-side vs
    // what must run locally on the returned rows. Server-side uses the
    // pushable subtree (or no filter at all when the entire tree is residual);
    // residual is applied afterwards via the in-process evaluator.
    const { pushable, residual } = resolvedFilter
      ? partitionFilter(resolvedFilter)
      : { pushable: undefined, residual: undefined };

    // buildInstanceQuery returns `unknown` — its inner table-expression
    // construction widens filters to `unknown[]` before TS can prove
    // TableExpressionFilterDefinition conformance. Cast at the boundary
    // where we hand it to runInstanceQuery so the consumer sees the real
    // CDF QueryRequest contract.
    const cdfParams = { ...params, filters: pushable };
    const instanceQuery = buildInstanceQuery(cdfParams, view) as QueryRequest;
    const data = await runInstanceQuery(instanceQuery, client);

    const columns = buildColumnSchemas(view, undefined, data);
    const baseDataset = createDataset({
      rows: data,
      columns,
    });

    // Apply the residual (non-pushable) filter locally, if any. We evaluate
    // it directly against the rows rather than wrapping in transformStep —
    // the residual is already a ResolvedRootFilter and transformStep would
    // try to re-resolve via the LLM-facing schema path.
    const final = residual
      ? { ...baseDataset, rows: baseDataset.rows.filter((row) => evaluateFilterTree(row, residual)) }
      : baseDataset;

    // Build non-fatal warnings for the LLM:
    // - CDF may hit the row budget before the residual filter narrows it —
    //   matches beyond the budget are invisible.
    // - A successful query that returned zero rows is a valid result, not an
    //   error; tell the LLM why the set is empty so it can reason about the
    //   filter rather than retrying blindly.
    const limitReached = residual !== undefined && data.length >= params.limit;
    const warnings = [
      ...(final.meta?.warnings ?? []),
      ...(limitReached
        ? [
            `Retrieved the maximum of ${params.limit} rows from CDF before applying client-side filter for non-pushable ops. Matches beyond the first ${params.limit} rows are not included. Narrow the pushable filter or raise "limit" if this matters.`,
          ]
        : []),
      ...(final.rows.length === 0
        ? [
            `Query returned 0 rows from view "${params.viewExternalId}". Verify the filter values are correct, or query without filters to confirm data exists. For instanceId-typed filters, check that the value includes the required prefix (e.g. "well:", "string:").`,
          ]
        : []),
    ];

    return {
      ...final,
      meta: {
        ...final.meta,
        ...(warnings.length > 0 ? { warnings } : {}),
      },
    };
  },
};
